import { Card } from "@/components/Card";
import { AlertModal } from "@/components/generic/AlertModal";
import { Button } from "@/components/generic/Button";
import { Heading } from "@/components/generic/Heading";
import { Menu, MenuItem } from "@/components/generic/Menu";
import { PageContent } from "@/components/layout/MainWindow";
import { PageHeader } from "@/components/layout/PageHeader";
import {
  getAvailableIntegrationsQueryOptions,
  getIntegrationConfigurationMetadataQueryOptions,
  getIntegrationConnectionInfoQueryOptions,
  getIntegrationSettingsQueryOptions,
  getIntegrationsQueryOptions,
  getProviderCapabilitiesQueryOptions,
  useAvailableIntegrationsQuery,
  useDeleteIntegrationMutation,
  useIntegrationConnectionInfoQuery,
  useIntegrationSettingsQuery,
} from "@/queries/integrations";
import { queryClient } from "@/utils/api";
import { testIntegrationConnection } from "@/utils/integration-helpers";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import {
  Check,
  CircleAlert,
  Ellipsis,
  LoaderCircle,
  TrashIcon,
} from "lucide-react";
import {
  CustomComponentPropsWithRef,
  JSX,
  useEffect,
  useMemo,
  useState,
} from "react";
import { MenuTrigger } from "react-aria-components";
import { toast } from "sonner";

export const Route = createFileRoute(
  "/_dashboard/settings/_admin/integrations/$integration/settings",
)({
  component: RouteComponent,
  loader: async ({ params, context }) => {
    const { integration } = params;

    await context.queryClient.prefetchQuery(
      getIntegrationConfigurationMetadataQueryOptions(integration),
    );
    await context.queryClient.prefetchQuery(
      getIntegrationConnectionInfoQueryOptions(integration),
    );
    await context.queryClient.prefetchQuery(
      getIntegrationSettingsQueryOptions(integration),
    );
    await context.queryClient.prefetchQuery(getIntegrationsQueryOptions());
    await context.queryClient.prefetchQuery(
      getAvailableIntegrationsQueryOptions(),
    );
  },
});

function RouteComponent() {
  const { integration } = Route.useParams();
  const navigate = Route.useNavigate();

  const { data: connectionInfo } =
    useIntegrationConnectionInfoQuery(integration);
  const { data: availableIntegrations = [] } = useAvailableIntegrationsQuery();
  const { data: settings = [] } = useIntegrationSettingsQuery(integration);
  const deleteMutation = useDeleteIntegrationMutation();
  const [checkingConnection, setCheckingConnection] = useState(false);
  const [verifiedConnection, setVerifiedConnection] = useState(false);
  const [isDeleteIntegration, setIsDeleteIntegration] = useState(false);

  const hasSettings = useMemo(() => {
    return settings.length > 0;
  }, [settings]);

  const currentIntegration = useMemo(() => {
    const found = availableIntegrations.find(
      (i) => i.uniqueName.toLowerCase() === integration.toLowerCase(),
    );
    if (!found) throw redirect({ to: "/settings/integrations" });
    return found;
  }, [integration, availableIntegrations]);

  const { data: capabilities } = useQuery(
    getProviderCapabilitiesQueryOptions(integration),
    queryClient,
  );

  const deleteIntegration = async () => {
    toast.promise(
      deleteMutation.mutateAsync(integration).then(() => {
        navigate({
          to: "/settings/integrations",
          search: { ignoreRedirect: true },
          mask: { to: "/settings/integrations" },
        });
      }),
      {
        loading: "Sletter integration...",
        success: "Integration slettet",
        error: "Fejl ved sletning af integration",
      },
    );
    setIsDeleteIntegration(false);
  };

  useEffect(() => {
    setCheckingConnection(true);
    testIntegrationConnection(
      currentIntegration,
      connectionInfo,
      false,
      true,
    ).then((result) => {
      setCheckingConnection(false);
      setVerifiedConnection(result);
    });
  }, [currentIntegration, connectionInfo]);

  const connectionText = useMemo(() => {
    if (checkingConnection) return "Forbinder...";
    if (verifiedConnection) return "Forbindelse oprettet";
    return (
      <>
        <span>Noget gik galt</span>
        <CircleAlert className="ml-2 size-4 text-red-500" />
      </>
    );
  }, [checkingConnection, verifiedConnection]);
  // const connect;
  if (!connectionInfo) return null;

  if (isDeleteIntegration) {
    return (
      <AlertModal
        title="Slet integration?"
        description={`Er du sikker på, at du vil slette integrationen "${currentIntegration.name}"? Dette kan ikke fortrydes.`}
        onConfirm={deleteIntegration}
        onClose={() => setIsDeleteIntegration(false)}
        isOpen={isDeleteIntegration}
        icon={<TrashIcon className="text-theme-active" />}
      />
    );
  }

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title={`Indstillinger for ${currentIntegration.name}`}
        backLinkOptions={{ to: "/settings/integrations" }}
      />
      <PageContent className="p-14 pt-21">
        <div className="mx-auto flex max-w-112 flex-col gap-y-8">
          <div className="flex items-center gap-x-6">
            <span
              className="text-theme-on-surface-variant *:size-14"
              dangerouslySetInnerHTML={{ __html: currentIntegration.iconSVG }}
            />
            <Heading level={2}>{currentIntegration.name}</Heading>
            <MenuTrigger>
              <Button
                intent="tertiary"
                size="rounded"
                className="ml-auto min-w-auto"
              >
                <Ellipsis className="m-2 size-6" />
              </Button>
              <Menu placement="bottom end">
                <MenuItem
                  onAction={() =>
                    navigate({
                      to: "/settings/integrations",
                      search: { ignoreRedirect: true },
                      mask: { to: "/settings/integrations" },
                    })
                  }
                >
                  Opsæt endnu en integration
                </MenuItem>
                <MenuItem
                  onAction={() => setIsDeleteIntegration(true)}
                  className="text-red-600"
                >
                  Slet integration
                </MenuItem>
              </Menu>
            </MenuTrigger>
          </div>
          <IntegrationSection>
            <div className="flex items-center justify-center">
              <div className="flex items-center text-sm leading-snug">
                {connectionText}
              </div>
              {checkingConnection ? (
                <LoaderCircle className="ml-auto size-6 animate-spin" />
              ) : verifiedConnection ? (
                <Check className="ml-auto size-6 text-green-600 dark:text-green-400" />
              ) : (
                <Button
                  intent="secondary"
                  className="ml-auto"
                  link={{
                    to: "/settings/integrations/$integration/connect",
                    search: { mode: "update" },
                    mask: {
                      to: "/settings/integrations/$integration/connect",
                      params: { integration },
                    },
                  }}
                >
                  Forbind igen
                </Button>
              )}
            </div>
          </IntegrationSection>
          {!capabilities?.storage && (
            <IntegrationSection
              title="Felter"
              description={`Informationer som overføres fra Lasso til ${currentIntegration.name}`}
              buttonProps={{
                link: {
                  to: "/settings/integrations/$integration/$mode",
                  search: (prev) => ({ ...prev, step: "fields" }),
                  params: { integration, mode: "update" },
                },
                isDisabled: !verifiedConnection,
              }}
              buttonText="Tilpas"
            />
          )}
          {!capabilities?.storage && (
            <IntegrationSection
              title="Notifikationer"
              description={`Modtag notifikationer når dataoverførsel fra Lasso til ${currentIntegration.name} ikke lykkedes`}
              buttonProps={{
                link: {
                  to: "/settings/integrations/$integration/$mode",
                  search: (prev) => ({ ...prev, step: "notifications" }),
                  params: { integration, mode: "update" },
                },
                isDisabled: !verifiedConnection,
              }}
              buttonText="Tilpas"
            />
          )}
          {hasSettings && (
            <IntegrationSection
              title="Indstillinger"
              description={`Rediger indstillinger specifikt til ${currentIntegration.name}`}
              buttonProps={{
                link: {
                  to: "/settings/integrations/$integration/$mode",
                  params: { integration, mode: "update" },
                  search: (prev) => ({ ...prev, step: "settings" }),
                },
                isDisabled: !verifiedConnection,
              }}
              buttonText="Tilpas"
            />
          )}
        </div>
      </PageContent>
    </div>
  );
}

type IntegrationSectionProps = {
  title: string;
  description?: string;
  buttonProps?: Exclude<
    CustomComponentPropsWithRef<typeof Button>,
    "intent" | "className"
  >;
  buttonText?: string;
  children?: React.ReactNode;
};

function IntegrationSection(
  props: Required<Pick<IntegrationSectionProps, "children">>,
): JSX.Element;
function IntegrationSection(
  props: Omit<IntegrationSectionProps, "children">,
): JSX.Element;
function IntegrationSection({
  title,
  description,
  buttonProps,
  buttonText,
  children,
}: Partial<IntegrationSectionProps>) {
  return (
    <Card theme="border" className="rounded-md p-8">
      {children ?? (
        <div className="flex items-center">
          <div className="flex flex-col gap-y-2">
            <div className="text-sm leading-snug">{title}</div>
            {description && (
              <div className="text-theme-on-background-variant text-xs leading-snug">
                {description}
              </div>
            )}
          </div>
          {buttonProps && (
            <Button
              intent="secondary"
              className="ml-12 min-w-auto"
              {...buttonProps}
            >
              {buttonText}
            </Button>
          )}
        </div>
      )}
    </Card>
  );
}
